import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { startTracker, trackPageView, track } from "@/lib/analytics";

// Public marketing routes we track. The authenticated app and /admin are internal
// and additionally excluded server-side (logged-in users are dropped).
const PUBLIC_PREFIXES = [
  "/landing", "/pricing", "/who-we-are", "/features", "/how-it-works", "/beta", "/request-demo",
  "/signup", "/login", "/forgot-password", "/reset-password",
];

function isPublic(path) {
  if (path === "/") return true;
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p));
}

export default function AnalyticsProvider() {
  const location = useLocation();
  const observerRef = useRef(null);
  const seenRef = useRef(new Set());

  useEffect(() => { startTracker(); }, []);

  // Page view on every public route change.
  useEffect(() => {
    if (!isPublic(location.pathname)) return;
    trackPageView();
    // (Re)wire an IntersectionObserver for impression-tracked elements.
    seenRef.current = new Set();
    if (observerRef.current) observerRef.current.disconnect();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const key = el.getAttribute("data-analytics-impression");
        if (key && !seenRef.current.has(key)) {
          seenRef.current.add(key);
          track("impression", { section: key.slice(0, 60) });
          io.unobserve(el);
        }
      });
    }, { threshold: 0.4 });
    // Defer so lazy content mounts first.
    const t = setTimeout(() => {
      document.querySelectorAll("[data-analytics-impression]").forEach((el) => io.observe(el));
    }, 400);
    observerRef.current = io;
    return () => { clearTimeout(t); io.disconnect(); };
  }, [location.pathname]);

  return null;
}
